import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, Square, Volume2, VolumeX, Shuffle, Repeat, List, X, Minus } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

// Classic songs playlist with free audio URLs
// Using royalty-free music that evokes the 90s/2000s era
const playlist = [
  { 
    id: 1, 
    title: 'Sandstorm', 
    artist: 'Darude', 
    duration: '3:45',
    // Using a placeholder beat - in production you'd use licensed music
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
  },
  { 
    id: 2, 
    title: 'Blue (Da Ba Dee)', 
    artist: 'Eiffel 65', 
    duration: '4:16',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3'
  },
  { 
    id: 3, 
    title: 'Around the World', 
    artist: 'Daft Punk', 
    duration: '7:09',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3'
  },
  { 
    id: 4, 
    title: 'Better Off Alone', 
    artist: 'Alice Deejay', 
    duration: '3:35',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3'
  },
  { 
    id: 5, 
    title: 'Kernkraft 400', 
    artist: 'Zombie Nation', 
    duration: '3:32',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3'
  },
  { 
    id: 6, 
    title: 'Children', 
    artist: 'Robert Miles', 
    duration: '4:10',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3'
  },
  { 
    id: 7, 
    title: 'Insomnia', 
    artist: 'Faithless', 
    duration: '6:27',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3'
  },
  { 
    id: 8, 
    title: 'What Is Love', 
    artist: 'Haddaway', 
    duration: '4:31',
    audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3'
  },
];

export default function Winamp({ 
  zIndex, 
  onClose, 
  onMinimize,
  onFocus,
  position = { x: 500, y: 80 },
  onPositionChange
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(75);
  const [isMuted, setIsMuted] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const dragRef = useRef(null);
  const audioRef = useRef(null);
  
  const track = playlist[currentTrack];
  
  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
      
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration);
        setIsLoading(false);
      });
      
      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current.currentTime);
      });
      
      audioRef.current.addEventListener('ended', () => {
        handleNext();
      });
      
      audioRef.current.addEventListener('error', (e) => {
        console.log('Audio error:', e);
        setIsLoading(false);
      });
      
      audioRef.current.addEventListener('canplay', () => {
        setIsLoading(false);
      });
      
      audioRef.current.addEventListener('waiting', () => {
        setIsLoading(true);
      });
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);
  
  // Load track when changed
  useEffect(() => {
    if (audioRef.current && track.audioUrl) {
      setIsLoading(true);
      audioRef.current.src = track.audioUrl;
      audioRef.current.load();
      
      if (isPlaying) {
        audioRef.current.play().catch(e => {
          console.log('Autoplay prevented:', e);
          setIsPlaying(false);
        });
      }
    }
  }, [currentTrack]);
  
  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume / 100;
    }
  }, [volume, isMuted]);
  
  const handlePlay = () => {
    playSound(SOUNDS.click);
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(e => {
        console.log('Play failed:', e);
      });
    }
  };
  
  const handleStop = () => {
    playSound(SOUNDS.click);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
  };
  
  const handlePrev = () => {
    playSound(SOUNDS.click);
    if (currentTime > 3) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      setCurrentTime(0);
    } else {
      const newTrack = currentTrack === 0 ? playlist.length - 1 : currentTrack - 1;
      setCurrentTrack(newTrack);
      setCurrentTime(0);
    }
  };
  
  const handleNext = () => {
    playSound(SOUNDS.click);
    let nextTrack;
    
    if (shuffle) {
      do {
        nextTrack = Math.floor(Math.random() * playlist.length);
      } while (nextTrack === currentTrack && playlist.length > 1);
    } else if (repeat) {
      nextTrack = currentTrack;
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
      return;
    } else {
      nextTrack = (currentTrack + 1) % playlist.length;
    }
    
    setCurrentTrack(nextTrack);
    setCurrentTime(0);
  };
  
  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };
  
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };
  
  // Dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.winamp-control')) return;
    setIsDragging(true);
    const rect = dragRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
    onFocus?.();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      onPositionChange?.({ x: Math.max(0, newX), y: Math.max(0, newY) });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, onPositionChange]);

  // Visualization bars
  const [bars, setBars] = useState(Array(20).fill(0));
  useEffect(() => {
    if (!isPlaying) {
      setBars(Array(20).fill(0));
      return;
    }
    const interval = setInterval(() => {
      setBars(prev => prev.map(() => Math.random() * 100));
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Calculate total playlist duration
  const totalPlaylistDuration = playlist.reduce((acc, song) => {
    const [min, sec] = song.duration.split(':').map(Number);
    return acc + min * 60 + sec;
  }, 0);
  
  const formatTotalDuration = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      ref={dragRef}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        zIndex,
        width: '275px',
        userSelect: 'none'
      }}
      onClick={onFocus}
    >
      {/* Main Player Window */}
      <div style={{
        background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
        border: '2px solid #444',
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
      }}>
        {/* Title Bar */}
        <div 
          onMouseDown={handleMouseDown}
          style={{
            background: 'linear-gradient(180deg, #4a4a4a 0%, #2a2a2a 100%)',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: isDragging ? 'grabbing' : 'grab',
            borderBottom: '1px solid #555'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: '#0f0', fontSize: '10px', fontWeight: 'bold', fontFamily: 'monospace' }}>
              WINAMP
            </span>
            {isLoading && (
              <span style={{ color: '#ff0', fontSize: '8px', fontFamily: 'monospace' }}>
                Loading...
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '2px' }}>
            <button
              className="winamp-control"
              onClick={onMinimize}
              style={{
                width: '14px',
                height: '14px',
                background: '#555',
                border: '1px solid #666',
                borderRadius: '2px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              <Minus size={8} color="#ccc" />
            </button>
            <button
              className="winamp-control"
              onClick={onClose}
              style={{
                width: '14px',
                height: '14px',
                background: '#555',
                border: '1px solid #666',
                borderRadius: '2px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0
              }}
            >
              <X size={8} color="#ccc" />
            </button>
          </div>
        </div>

        {/* Display */}
        <div style={{
          background: '#000',
          margin: '4px',
          padding: '8px',
          borderRadius: '2px',
          border: '1px solid #333'
        }}>
          {/* Visualization */}
          <div style={{
            height: '30px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
            marginBottom: '8px',
            background: '#0a0a0a',
            padding: '4px',
            borderRadius: '2px'
          }}>
            {bars.map((height, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: `${height}%`,
                  background: height > 70 ? '#ff0' : height > 40 ? '#0f0' : '#0a0',
                  transition: 'height 0.1s',
                  minHeight: '2px'
                }}
              />
            ))}
          </div>
          
          {/* Track Info */}
          <div style={{ color: '#0f0', fontFamily: 'monospace', fontSize: '11px' }}>
            <div style={{ 
              whiteSpace: 'nowrap', 
              overflow: 'hidden',
              marginBottom: '4px'
            }}>
              <motion.span
                animate={isPlaying ? { x: [0, -100, 0] } : {}}
                transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                style={{ display: 'inline-block' }}
              >
                {track.artist} - {track.title}
              </motion.span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0a0' }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration) || track.duration}</span>
            </div>
          </div>
          
          {/* Seek Bar */}
          <div style={{ marginTop: '8px' }}>
            <div 
              style={{
                height: '8px',
                background: '#222',
                borderRadius: '2px',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={handleSeek}
            >
              <div style={{
                width: duration ? `${(currentTime / duration) * 100}%` : '0%',
                height: '100%',
                background: 'linear-gradient(90deg, #0f0 0%, #ff0 100%)',
                borderRadius: '2px',
                transition: 'width 0.2s'
              }} />
            </div>
          </div>
        </div>

        {/* Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          padding: '8px',
          background: '#1a1a1a'
        }}>
          {/* Previous */}
          <button
            onClick={handlePrev}
            style={{
              width: '28px',
              height: '20px',
              background: 'linear-gradient(180deg, #555 0%, #333 100%)',
              border: '1px solid #666',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SkipBack size={12} color="#ccc" />
          </button>
          
          {/* Play/Pause */}
          <button
            onClick={handlePlay}
            style={{
              width: '28px',
              height: '20px',
              background: isPlaying 
                ? 'linear-gradient(180deg, #0a0 0%, #060 100%)'
                : 'linear-gradient(180deg, #555 0%, #333 100%)',
              border: '1px solid #666',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isPlaying ? <Pause size={12} color="#fff" /> : <Play size={12} color="#ccc" />}
          </button>
          
          {/* Stop */}
          <button
            onClick={handleStop}
            style={{
              width: '28px',
              height: '20px',
              background: 'linear-gradient(180deg, #555 0%, #333 100%)',
              border: '1px solid #666',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Square size={10} color="#ccc" fill="#ccc" />
          </button>
          
          {/* Next */}
          <button
            onClick={handleNext}
            style={{
              width: '28px',
              height: '20px',
              background: 'linear-gradient(180deg, #555 0%, #333 100%)',
              border: '1px solid #666',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <SkipForward size={12} color="#ccc" />
          </button>

          <div style={{ width: '8px' }} />

          {/* Shuffle */}
          <button
            onClick={() => { playSound(SOUNDS.click); setShuffle(!shuffle); }}
            style={{
              width: '24px',
              height: '18px',
              background: shuffle 
                ? 'linear-gradient(180deg, #a50 0%, #630 100%)'
                : 'linear-gradient(180deg, #444 0%, #222 100%)',
              border: '1px solid #555',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Shuffle size={10} color={shuffle ? '#ff0' : '#888'} />
          </button>
          
          {/* Repeat */}
          <button
            onClick={() => { playSound(SOUNDS.click); setRepeat(!repeat); }}
            style={{
              width: '24px',
              height: '18px',
              background: repeat 
                ? 'linear-gradient(180deg, #a50 0%, #630 100%)'
                : 'linear-gradient(180deg, #444 0%, #222 100%)',
              border: '1px solid #555',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Repeat size={10} color={repeat ? '#ff0' : '#888'} />
          </button>
        </div>

        {/* Volume */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 8px 8px',
          background: '#1a1a1a'
        }}>
          <button
            onClick={() => { playSound(SOUNDS.click); setIsMuted(!isMuted); }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px'
            }}
          >
            {isMuted ? <VolumeX size={14} color="#888" /> : <Volume2 size={14} color="#0f0" />}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            style={{
              flex: 1,
              height: '4px',
              accentColor: '#0f0',
              cursor: 'pointer'
            }}
          />
          <span style={{ 
            color: '#0f0', 
            fontSize: '10px', 
            fontFamily: 'monospace',
            width: '24px'
          }}>
            {isMuted ? '0' : volume}%
          </span>
          
          <button
            onClick={() => { playSound(SOUNDS.click); setShowPlaylist(!showPlaylist); }}
            style={{
              width: '24px',
              height: '18px',
              background: showPlaylist 
                ? 'linear-gradient(180deg, #050 0%, #030 100%)'
                : 'linear-gradient(180deg, #444 0%, #222 100%)',
              border: '1px solid #555',
              borderRadius: '2px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '4px'
            }}
          >
            <List size={10} color={showPlaylist ? '#0f0' : '#888'} />
          </button>
        </div>
      </div>

      {/* Playlist Window */}
      <AnimatePresence>
        {showPlaylist && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              marginTop: '2px',
              background: 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)',
              border: '2px solid #444',
              borderRadius: '2px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          >
            <div style={{
              background: '#000',
              margin: '4px',
              maxHeight: '180px',
              overflowY: 'auto',
              borderRadius: '2px',
              border: '1px solid #333'
            }}>
              {playlist.map((song, index) => (
                <div
                  key={song.id}
                  onClick={() => {
                    playSound(SOUNDS.click);
                    setCurrentTrack(index);
                    setCurrentTime(0);
                    // Auto-play when selecting from playlist
                    setTimeout(() => {
                      if (audioRef.current) {
                        audioRef.current.play().then(() => {
                          setIsPlaying(true);
                        }).catch(e => console.log('Play failed:', e));
                      }
                    }, 100);
                  }}
                  style={{
                    padding: '4px 8px',
                    cursor: 'pointer',
                    background: currentTrack === index ? '#003300' : 'transparent',
                    borderBottom: '1px solid #222',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span style={{
                    color: currentTrack === index ? '#0f0' : '#0a0',
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}>
                    {index + 1}. {song.artist} - {song.title}
                  </span>
                  <span style={{
                    color: '#080',
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    marginLeft: '8px'
                  }}>
                    {song.duration}
                  </span>
                </div>
              ))}
            </div>
            <div style={{
              padding: '4px 8px',
              display: 'flex',
              justifyContent: 'space-between',
              color: '#0a0',
              fontSize: '9px',
              fontFamily: 'monospace'
            }}>
              <span>{playlist.length} tracks</span>
              <span>Total: {formatTotalDuration(totalPlaylistDuration)}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
